import { type FC, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import DeckGL from '@deck.gl/react';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import { _GlobeView as GlobeView, PostProcessEffect } from '@deck.gl/core';
import { ScatterplotLayer, PathLayer, ArcLayer, BitmapLayer, IconLayer } from '@deck.gl/layers';
import { TileLayer } from '@deck.gl/geo-layers';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyLayer = any;
import { Map, type MapRef } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';

import { useAppStore } from '@/stores/app-store';
import {
  MAP_STYLE,
  INITIAL_VIEW_STATE,
  magnitudeToRadius,
  magnitudeToColor,
  WAR_ZONE_COLOR, WAR_ZONE_STROKE,
  AIRCRAFT_COLOR, MILITARY_AIRCRAFT_COLOR,
  SHIP_COLOR,
  NAVAL_COLOR, NAVAL_STROKE,
  FIGHTER_COLOR,
  POWER_PLANT_COLORS,
  DRONE_ZONE_COLOR, DRONE_ZONE_STROKE,
  NUCLEAR_ASSET_COLOR,
  CABLE_COLORS,
  SATELLITE_TILES,
  NIGHT_LIGHTS_TILES,
  GIBS_RECENT_TILES,
} from '@/config/map-styles';
import type { WindyWebcam } from '@/services/windy-webcams';
import { PUBLIC_CAMS } from '@/config/webcam-sources';
import type {
  Earthquake, Wildfire, Aircraft, Ship, NavalVessel,
  FighterJet, PowerPlant, ConflictZone, DroneZone, NuclearAsset, SubmarineCable,
  TroopMovement, MissileTrajectory, RichTooltip, CyberThreat, DroneAttack,
  WorldCity, StrategicChokepoint, CarrierGroup, CountryProfile, AcledEvent, NotamItem,
} from '@/types';
import { ACLED_COLORS } from '@/services/acled';
import { NOTAM_CATEGORY_COLORS } from '@/services/notam';
import { useMissileAnimation } from '@/hooks/useMissileAnimation';
import { timeAgo } from '@/utils/format';
import { useSimMissileAnimation } from '@/hooks/useSimMissileAnimation';
import { useDroneAnimation } from '@/hooks/useDroneAnimation';
import { SENSOR_PASSES, type SensorMode } from '@/components/map/sensor-optics';
import { getGlobeBasemap } from '@/config/map-styles';
import { DRONE_TYPE_LABELS } from '@/services/drone-attacks';
import { fetchAircraftRoute, getCachedRoute } from '@/services/aircraft-route';

// ---------------------------------------------------------------------------
// Interactive deck.gl + MapLibre world map with full layer suite
// ---------------------------------------------------------------------------

const MOVEMENT_TYPE_COLORS: Record<string, [number, number, number]> = {
  ground:    [251, 146, 60],   // orange
  naval:     [56, 189, 248],   // sky blue
  air:       [167, 139, 250],  // purple
  logistics: [74, 222, 128],   // green
};

const MISSILE_TYPE_COLORS: Record<string, [number, number, number]> = {
  ballistic:   [239, 68, 68],  // red
  cruise:      [251, 146, 60], // orange
  hypersonic:  [236, 72, 153], // pink
  drone:       [255, 183, 45], // neon amber — 스웜 드론
};


// ---------------------------------------------------------------------------
// 항공기 아이콘 — 위에서 본 실루엣(기수가 북쪽). mask:true 라서 흰색으로 그리고
// getColor 로 틴팅한다. 별도 에셋 파일 없이 data URI 로 인라인.
// ---------------------------------------------------------------------------
const PLANE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64"><path d="M32 3 L35.5 20 L61 40 L61 46.5 L35.5 38 L35.5 52 L43 58 L43 62.5 L32 58.5 L21 62.5 L21 58 L28.5 52 L28.5 38 L3 46.5 L3 40 L28.5 20 Z" fill="#ffffff"/></svg>`;
const PLANE_ICON = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(PLANE_SVG)}`;
const PLANE_ICON_MAPPING = {
  plane: { x: 0, y: 0, width: 64, height: 64, anchorX: 32, anchorY: 32, mask: true },
} as const;

/** 피트 → "37,000 ft (11,278 m)" */
function formatAltitude(ft: number): string {
  return `${Math.round(ft).toLocaleString()} ft (${Math.round(ft * 0.3048).toLocaleString()} m)`;
}

/** 노트 → "487 kt (902 km/h)" */
function formatSpeed(kt: number): string {
  return `${Math.round(kt).toLocaleString()} kt (${Math.round(kt * 1.852).toLocaleString()} km/h)`;
}

function RichTooltipPanel({ tip }: { tip: RichTooltip }) {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1440;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 900;
  const maxW = Math.min(340, vw - 20);          // never wider than screen
  const left = Math.max(8, Math.min(tip.x + 14, vw - maxW - 8));   // clamp right edge
  const top  = Math.max(8, Math.min(tip.y - 60, vh - 250));         // clamp bottom edge
  return (
    <div
      className="pointer-events-none fixed z-50 rounded bg-gray-950/97 backdrop-blur-md border border-red-900/40 shadow-2xl shadow-black/60 text-xs"
      style={{ left, top, maxWidth: maxW, minWidth: Math.min(220, maxW) }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 px-3 pt-2.5 pb-1.5 border-b border-gray-800/60">
        <div>
          <div className="font-bold text-slate-100 text-[11px] leading-tight">{tip.header}</div>
          {tip.subheader && (
            <div className="text-[10px] text-slate-400 mt-0.5">{tip.subheader}</div>
          )}
        </div>
        {tip.badgeText && (
          <span className={`shrink-0 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide ${
            tip.badgeColor === 'red' ? 'bg-red-950 text-red-300 border border-red-800' :
            tip.badgeColor === 'orange' ? 'bg-orange-950 text-orange-300 border border-orange-800' :
            tip.badgeColor === 'yellow' ? 'bg-yellow-950 text-yellow-300 border border-yellow-800' :
            tip.badgeColor === 'blue' ? 'bg-blue-950 text-blue-300 border border-blue-800' :
            'bg-emerald-950 text-emerald-300 border border-emerald-800'
          }`}>{tip.badgeText}</span>
        )}
      </div>

      {/* Details */}
      {tip.details.length > 0 && (
        <div className="px-3 py-1.5 space-y-0.5">
          {tip.details.map((d, i) => (
            <div key={i} className="flex justify-between gap-3">
              <span className="text-slate-500">{d.label}</span>
              <span className={d.highlight ? 'text-amber-300 font-semibold' : 'text-slate-300'}>{d.value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Related News */}
      {tip.newsItems && tip.newsItems.length > 0 && (
        <div className="border-t border-gray-800/60 px-3 py-1.5">
          <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-1">Related Intelligence</div>
          <div className="space-y-1">
            {tip.newsItems.slice(0, 3).map((n, i) => (
              <div key={i} className="text-[10px] text-slate-300 leading-tight">
                <span className="text-slate-500">[{n.source}]</span> {n.title}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export const WorldMap: FC = () => {
  const mapRef = useRef<MapRef>(null);
  const viewState = useAppStore((s) => s.viewState);
  const setViewState = useAppStore((s) => s.setViewState);
  const earthquakes = useAppStore((s) => s.earthquakes);
  const wildfires = useAppStore((s) => s.wildfires);
  const aircraft = useAppStore((s) => s.aircraft);
  const ships = useAppStore((s) => s.ships);
  const navalVessels = useAppStore((s) => s.navalVessels);
  const fighterJets = useAppStore((s) => s.fighterJets);
  const powerPlants = useAppStore((s) => s.powerPlants);
  const conflictZones = useAppStore((s) => s.conflictZones);
  const droneZones = useAppStore((s) => s.droneZones);
  const nuclearAssets = useAppStore((s) => s.nuclearAssets);
  const submarineCables = useAppStore((s) => s.submarineCables);
  const troopMovements = useAppStore((s) => s.troopMovements);
  const missiles = useAppStore((s) => s.missiles);
  const droneAttacks = useAppStore((s) => s.droneAttacks);
  // Simulation overlay data — when simulation is active, REPLACE real data
  const simMissiles = useAppStore((s) => s.simMissiles);
  const simTroopMovements = useAppStore((s) => s.simTroopMovements);
  const simConflictZones = useAppStore((s) => s.simConflictZones);
  const isSimActive = useAppStore((s) => s.simulation.active);
  const cyberThreats = useAppStore((s) => s.cyberThreats);
  const acledEvents = useAppStore((s) => s.acledEvents);
  const notams = useAppStore((s) => s.notams);
  const militaryAircraft = useAppStore((s) => s.militaryAircraft);
  const worldCities = useAppStore((s) => s.worldCities);
  const chokepoints = useAppStore((s) => s.chokepoints);
  const carrierGroups = useAppStore((s) => s.carrierGroups);
  const countryProfiles = useAppStore((s) => s.allCountries);
  const visibleLayers = useAppStore((s) => s.visibleLayers);
  const selectCountry = useAppStore((s) => s.selectCountry);
  const setCompareCountry = useAppStore((s) => s.setCompareCountry);
  const compareMode = useAppStore((s) => s.compareMode);
  const setCompareMode = useAppStore((s) => s.setCompareMode);
  const selectedCountry = useAppStore((s) => s.selectedCountry);
  const windyWebcams = useAppStore((s) => s.windyWebcams);
  const globeMode = useAppStore((s) => s.globeMode);
  const crisisAlerts = useAppStore((s) => s.crisisAlerts);
  const sensorMode = useAppStore((s) => s.sensorMode);
  const globeBasemap = useAppStore((s) => s.globeBasemap);
  const setCursorGeo = useAppStore((s) => s.setCursorGeo);
  const trafficCameras = useAppStore((s) => s.trafficCameras);
  const setSelectedCctvId = useAppStore((s) => s.setSelectedCctvId);
  const selectedAircraftId = useAppStore((s) => s.selectedAircraftId);
  const setSelectedAircraftId = useAppStore((s) => s.setSelectedAircraftId);

  // Reset viewState when entering globe mode so the whole earth fits on screen
  useEffect(() => {
    if (globeMode) {
      setViewState({ longitude: 10, latitude: 20, zoom: 2, pitch: 0, bearing: 0 });
    } else {
      setViewState({ longitude: 10, latitude: 20, zoom: 2, pitch: 0, bearing: 0 });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globeMode]);

  // Start missile and drone animation loops
  useMissileAnimation(visibleLayers.has('missiles'));
  useSimMissileAnimation(); // Animates simulation missiles independently
  useDroneAnimation(visibleLayers.has('drone-attacks'));

  // Satellite layer — Esri World Imagery tiles via MapLibre
  const showSatellite = visibleLayers.has('satellite');
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    const apply = () => {
      if (showSatellite) {
        if (!map.getSource('__satellite')) {
          map.addSource('__satellite', { type: 'raster', tiles: SATELLITE_TILES, tileSize: 256 });
          const layers = map.getStyle()?.layers ?? [];
          const firstSymbol = layers.find((l) => l.type === 'symbol')?.id;
          map.addLayer(
            { id: '__satellite-layer', type: 'raster', source: '__satellite', paint: { 'raster-opacity': 0.85 } },
            firstSymbol,
          );
        }
      } else {
        if (map.getLayer('__satellite-layer')) map.removeLayer('__satellite-layer');
        if (map.getSource('__satellite')) map.removeSource('__satellite');
      }
    };
    if (map.isStyleLoaded()) apply();
    else map.once('load', apply);
  }, [showSatellite]);

  // Night Lights layer — NASA GIBS VIIRS Black Marble (free, no API key)
  const showNightLights = visibleLayers.has('night-lights');
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    const apply = () => {
      if (showNightLights) {
        if (!map.getSource('__night-lights')) {
          map.addSource('__night-lights', {
            type: 'raster',
            tiles: NIGHT_LIGHTS_TILES,
            tileSize: 256,
            maxzoom: 8,
            attribution: 'NASA GIBS / VIIRS Black Marble',
          });
          const layers = map.getStyle()?.layers ?? [];
          const firstSymbol = layers.find((l) => l.type === 'symbol')?.id;
          map.addLayer(
            { id: '__night-lights-layer', type: 'raster', source: '__night-lights',
              paint: { 'raster-opacity': 0.85, 'raster-brightness-min': 0.1 } },
            firstSymbol,
          );
        }
      } else {
        if (map.getLayer('__night-lights-layer')) map.removeLayer('__night-lights-layer');
        if (map.getSource('__night-lights')) map.removeSource('__night-lights');
      }
    };
    if (map.isStyleLoaded()) apply();
    else map.once('load', apply);
  }, [showNightLights]);

  // Recent Satellite layer — NASA GIBS MODIS Terra (~3 days behind, free, no API key)
  const showRecentSat = visibleLayers.has('recent-satellite');
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    const apply = () => {
      if (showRecentSat) {
        if (!map.getSource('__recent-sat')) {
          map.addSource('__recent-sat', {
            type: 'raster',
            tiles: GIBS_RECENT_TILES,
            tileSize: 256,
            maxzoom: 9,
            attribution: 'NASA GIBS / MODIS Terra',
          });
          const layers = map.getStyle()?.layers ?? [];
          const firstSymbol = layers.find((l) => l.type === 'symbol')?.id;
          map.addLayer(
            { id: '__recent-sat-layer', type: 'raster', source: '__recent-sat',
              paint: { 'raster-opacity': 0.8 } },
            firstSymbol,
          );
        }
      } else {
        if (map.getLayer('__recent-sat-layer')) map.removeLayer('__recent-sat-layer');
        if (map.getSource('__recent-sat')) map.removeSource('__recent-sat');
      }
    };
    if (map.isStyleLoaded()) apply();
    else map.once('load', apply);
  }, [showRecentSat]);

  const [tooltip, setTooltip] = useState<RichTooltip | null>(null);
  const [selectedMapCam, setSelectedMapCam] = useState<WindyWebcam | null>(null);

  const onViewStateChange = useCallback(
    (params: { viewState?: object }) => {
      if (params.viewState) {
        setViewState(params.viewState as Parameters<typeof setViewState>[0]);
      }
    },
    [setViewState],
  );

  const setTip = useCallback((x: number, y: number, tip: Omit<RichTooltip, 'x' | 'y'>) => {
    setTooltip({ x, y, ...tip });
  }, []);
  const clearTip = useCallback(() => setTooltip(null), []);

  // Static layers (recomputed only when data changes)
  const staticLayers = useMemo(() => {
    const result: AnyLayer[] = [];

    // ── Conflict / War Zones ────────────────────────────────────────────────
    if (visibleLayers.has('war-zones')) {
      result.push(
        new ScatterplotLayer<ConflictZone>({
          id: 'war-zones',
          data: isSimActive ? simConflictZones : [...conflictZones, ...simConflictZones],
          pickable: true,
          opacity: 0.9,
          stroked: true,
          lineWidthMinPixels: 2,
          getPosition: (d) => [d.lon, d.lat],
          getRadius: (d) => d.radiusKm * 1000,
          getFillColor: (d) => {
            if (d.severity === 'critical') return [220, 38, 38, 55] as [number, number, number, number];
            if (d.severity === 'high')     return [249, 115, 22, 50] as [number, number, number, number];
            return WAR_ZONE_COLOR;
          },
          getLineColor: (d) => {
            if (d.severity === 'critical') return [239, 68, 68, 200] as [number, number, number, number];
            if (d.severity === 'high')     return [251, 146, 60, 180] as [number, number, number, number];
            return WAR_ZONE_STROKE;
          },
          radiusMinPixels: 8,
          radiusMaxPixels: 120,
          onHover: (info) => {
            if (info.object) {
              const z = info.object as ConflictZone;
              const statusLabel = z.status === 'ceasefire' ? '🕊 CEASEFIRE'
                : z.status === 'ended' ? '✅ ENDED'
                : z.status === 'negotiating' ? '🤝 NEGOTIATING'
                : '🔴 ACTIVE';
              setTip(info.x, info.y, {
                header: `⚔ ${z.name}`,
                subheader: z.ceasefireNotes ?? z.description,
                badgeText: z.status === 'ceasefire' ? 'CEASEFIRE' : z.status === 'ended' ? 'ENDED' : z.severity,
                badgeColor: z.status === 'ceasefire' ? 'blue' : z.status === 'ended' ? 'green' : z.severity === 'critical' ? 'red' : 'orange',
                details: [
                  { label: 'Status', value: statusLabel, highlight: z.status === 'ceasefire' || z.status === 'ended' },
                  { label: 'Parties', value: z.parties.join(' vs ') },
                  { label: 'Type', value: z.type.replace(/-/g, ' ').toUpperCase() },
                  { label: 'Since', value: z.startDate.slice(0, 7) },
                  ...(z.ceasefireDate ? [{ label: 'Ceasefire', value: z.ceasefireDate.slice(0, 10), highlight: true }] : []),
                ],
              });
            } else clearTip();
          },
        }),
      );
    }

    // ── Drone Zones ─────────────────────────────────────────────────────────
    if (visibleLayers.has('drone-zones')) {
      result.push(
        new ScatterplotLayer<DroneZone>({
          id: 'drone-zones',
          data: droneZones,
          pickable: true,
          opacity: 0.8,
          stroked: true,
          lineWidthMinPixels: 1,
          getPosition: (d) => [d.lon, d.lat],
          getRadius: (d) => d.radiusKm * 1000,
          getFillColor: DRONE_ZONE_COLOR,
          getLineColor: DRONE_ZONE_STROKE,
          radiusMinPixels: 6,
          radiusMaxPixels: 80,
          onHover: (info) => {
            if (info.object) {
              const d = info.object as DroneZone;
              setTip(info.x, info.y, {
                header: `Drone Zone: ${d.name}`,
                badgeText: d.type,
                badgeColor: 'orange',
                details: [
                  { label: 'Status', value: d.type.replace(/-/g, ' ').toUpperCase(), highlight: true },
                  { label: 'Severity', value: d.severity },
                  { label: 'Radius', value: `${d.radiusKm} km` },
                  ...(d.country ? [{ label: 'Country', value: d.country }] : []),
                ],
              });
            } else clearTip();
          },
        }),
      );
    }

    // ── Earthquakes ─────────────────────────────────────────────────────────
    if (visibleLayers.has('earthquakes')) {
      result.push(
        new ScatterplotLayer<Earthquake>({
          id: 'earthquakes',
          data: earthquakes,
          pickable: true,
          opacity: 0.8,
          stroked: true,
          lineWidthMinPixels: 1,
          getPosition: (d) => [d.lon, d.lat],
          getRadius: (d) => magnitudeToRadius(d.magnitude) * 1000,
          getFillColor: (d) => [...magnitudeToColor(d.magnitude), 180] as [number, number, number, number],
          getLineColor: [255, 255, 255, 60],
          radiusMinPixels: 4,
          radiusMaxPixels: 40,
          onHover: (info) => {
            if (info.object) {
              const eq = info.object as Earthquake;
              setTip(info.x, info.y, {
                header: `M${eq.magnitude} Earthquake`,
                subheader: eq.place,
                badgeText: eq.tsunami ? 'TSUNAMI' : undefined,
                badgeColor: 'red',
                details: [
                  { label: 'Magnitude', value: eq.magnitude.toString(), highlight: true },
                  { label: 'Depth', value: `${eq.depth} km` },
                  { label: 'Time', value: new Date(eq.timestamp).toUTCString().slice(0, 22) },
                  ...(eq.felt ? [{ label: 'Reports', value: eq.felt.toString() }] : []),
                ],
              });
            } else clearTip();
          },
        }),
      );
    }

    // NOTE: 구 'conflicts'/'news' GDELT 레이어 제거됨 — GDELT GEO API deprecated (항상 빈 배열).
    // 실제 분쟁 이벤트는 'acled' 레이어(ACLED + LiveUAMap)가 렌더링.

    // ── Wildfires ───────────────────────────────────────────────────────────
    if (visibleLayers.has('wildfires')) {
      result.push(
        new ScatterplotLayer<Wildfire>({
          id: 'wildfires',
          data: wildfires,
          pickable: true,
          opacity: 0.75,
          stroked: false,
          getPosition: (d) => [d.lon, d.lat],
          getRadius: (d) => Math.max(3000, d.brightness * 20),
          getFillColor: (d) =>
            d.brightness > 400 ? [239, 68, 68, 200] : [249, 115, 22, 170],
          radiusMinPixels: 3,
          radiusMaxPixels: 16,
          onHover: (info) => {
            if (info.object) {
              const wf = info.object as Wildfire;
              setTip(info.x, info.y, {
                header: 'Active Wildfire',
                subheader: wf.country,
                badgeText: wf.brightness > 400 ? 'INTENSE' : 'ACTIVE',
                badgeColor: wf.brightness > 400 ? 'red' : 'orange',
                details: [
                  { label: 'Brightness', value: `${wf.brightness} K`, highlight: wf.brightness > 400 },
                  { label: 'FRP', value: `${wf.frp} MW` },
                  { label: 'Satellite', value: wf.satellite },
                ],
              });
            } else clearTip();
          },
        }),
      );
    }

    // ── Civilian Aircraft ───────────────────────────────────────────────────
    if (visibleLayers.has('aircraft')) {
      result.push(
        new IconLayer<Aircraft>({
          id: 'aircraft',
          data: aircraft,
          pickable: true,
          iconAtlas: PLANE_ICON,
          iconMapping: PLANE_ICON_MAPPING,
          getIcon: () => 'plane',
          getPosition: (d) => [d.lon, d.lat],
          // deck.gl getAngle 은 반시계 기준, heading 은 시계 기준(북=0) → 부호 반전
          getAngle: (d) => -d.heading,
          sizeUnits: 'pixels',
          getSize: (d) => (d.id === selectedAircraftId ? 26 : 17),
          sizeMinPixels: 11,
          sizeMaxPixels: 30,
          getColor: (d) =>
            d.id === selectedAircraftId
              ? [255, 255, 255, 255]
              : d.isMilitary ? MILITARY_AIRCRAFT_COLOR : AIRCRAFT_COLOR,
          updateTriggers: {
            getSize: selectedAircraftId,
            getColor: selectedAircraftId,
          },
          onClick: (info) => {
            const a = info.object as Aircraft | undefined;
            if (!a) return false;
            setSelectedAircraftId(a.id);
            // 목적지는 ADS-B 에 없다 → 편명으로 노선 DB 조회 (결과는 모듈 캐시)
            void fetchAircraftRoute(a.callsign);
            return true;   // 국가 선택 등 다른 핸들러로 전파 차단
          },
          onHover: (info) => {
            if (info.object) {
              const a = info.object as Aircraft;
              const route = getCachedRoute(a.callsign);
              if (route === undefined) void fetchAircraftRoute(a.callsign);
              setTip(info.x, info.y, {
                header: a.callsign,
                subheader: [a.acDesc ?? a.acType, a.operator].filter(Boolean).join(' · ') || a.country,
                badgeText: a.isMilitary ? 'MILITARY' : 'CIVIL',
                badgeColor: a.isMilitary ? 'red' : 'blue',
                details: [
                  ...(route
                    ? [{
                        label: 'Route',
                        value: `${route.originIata ?? '???'} ${route.originCity ?? ''} → ${route.destIata ?? '???'} ${route.destCity ?? ''}`.replace(/\s+/g, ' ').trim(),
                        highlight: true,
                      }]
                    : []),
                  { label: 'Altitude', value: formatAltitude(a.altitudeFt), highlight: !route },
                  { label: 'Speed', value: formatSpeed(a.speedKt) },
                  { label: 'Heading', value: `${Math.round(a.heading)}°` },
                  ...(a.registration ? [{ label: 'Reg', value: a.registration }] : []),
                  { label: '', value: 'Click to track →' },
                ],
              });
            } else clearTip();
          },
        }),
      );
    }

    // ── Fighter Jets ────────────────────────────────────────────────────────
    if (visibleLayers.has('fighters')) {
      result.push(
        new ScatterplotLayer<FighterJet>({
          id: 'fighters',
          data: fighterJets,
          pickable: true,
          opacity: 0.95,
          stroked: true,
          lineWidthMinPixels: 1,
          getPosition: (d) => [d.lon, d.lat],
          getRadius: 5000,
          getFillColor: FIGHTER_COLOR,
          getLineColor: [255, 100, 100, 120],
          radiusMinPixels: 3,
          radiusMaxPixels: 10,
          onHover: (info) => {
            if (info.object) {
              const j = info.object as FighterJet;
              setTip(info.x, info.y, {
                header: `${j.aircraft} — ${j.callsign}`,
                subheader: j.country,
                badgeText: j.mission.toUpperCase(),
                badgeColor: j.mission === 'combat' || j.mission === 'strike' ? 'red' : 'orange',
                details: [
                  { label: 'Mission', value: j.mission.toUpperCase(), highlight: j.mission === 'combat' },
                  { label: 'Altitude', value: `${Math.round(j.altitude).toLocaleString()} m` },
                  { label: 'Country', value: j.country },
                ],
              });
            } else clearTip();
          },
        }),
      );
    }

    // ── Cargo/Civilian Ships ─────────────────────────────────────────────────
    if (visibleLayers.has('ships')) {
      result.push(
        new ScatterplotLayer<Ship>({
          id: 'ships',
          data: ships,
          pickable: true,
          opacity: 0.85,
          stroked: false,
          getPosition: (d) => [d.lon, d.lat],
          getRadius: 5000,
          // 실시간 AIS 는 민트, 시뮬레이션은 기존 블루 — 한눈에 구분되게
          getFillColor: (d) => (d.isLive ? [143, 247, 199, 215] : SHIP_COLOR),
          radiusMinPixels: 2,
          radiusMaxPixels: 7,
          onHover: (info) => {
            if (info.object) {
              const s = info.object as Ship;
              const ageSec = s.lastSeen ? Math.round((Date.now() - s.lastSeen) / 1000) : null;
              setTip(info.x, info.y, {
                header: s.name,
                subheader: [s.flag, s.callSign].filter(Boolean).join(' · '),
                badgeText: s.isLive ? 'LIVE AIS' : 'SIM',
                badgeColor: s.isLive ? 'green' : 'orange',
                details: [
                  { label: 'Speed', value: `${s.speed.toFixed(1)} kn (${(s.speed * 1.852).toFixed(0)} km/h)`, highlight: true },
                  { label: 'Heading', value: `${Math.round(s.heading)}°` },
                  { label: 'Type', value: s.type.toUpperCase() },
                  ...(s.destination ? [{ label: 'Dest', value: s.destination }] : []),
                  ...(s.mmsi ? [{ label: 'MMSI', value: String(s.mmsi) }] : []),
                  ...(ageSec != null ? [{ label: 'Age', value: ageSec < 60 ? `${ageSec}s ago` : `${Math.round(ageSec / 60)}m ago` }] : []),
                ],
              });
            } else clearTip();
          },
        }),
      );
    }

    // ── Naval / Military Vessels ─────────────────────────────────────────────
    if (visibleLayers.has('naval')) {
      result.push(
        new ScatterplotLayer<NavalVessel>({
          id: 'naval',
          data: navalVessels,
          pickable: true,
          opacity: 0.9,
          stroked: true,
          lineWidthMinPixels: 1,
          getPosition: (d) => [d.lon, d.lat],
          getRadius: 8000,
          getFillColor: NAVAL_COLOR,
          getLineColor: NAVAL_STROKE,
          radiusMinPixels: 4,
          radiusMaxPixels: 14,
          onHover: (info) => {
            if (info.object) {
              const v = info.object as NavalVessel;
              setTip(info.x, info.y, {
                header: v.name,
                subheader: v.groupName,
                badgeText: v.type.toUpperCase(),
                badgeColor: 'blue',
                details: [
                  { label: 'Class', value: v.type.toUpperCase(), highlight: v.type === 'carrier' },
                  { label: 'Country', value: v.country },
                  ...(v.groupName ? [{ label: 'Group', value: v.groupName }] : []),
                ],
              });
            } else clearTip();
          },
        }),
      );
    }

    // ── Power Plants ─────────────────────────────────────────────────────────
    if (visibleLayers.has('power-plants')) {
      result.push(
        new ScatterplotLayer<PowerPlant>({
          id: 'power-plants',
          data: powerPlants,
          pickable: true,
          opacity: 0.9,
          stroked: true,
          lineWidthMinPixels: 1,
          getPosition: (d) => [d.lon, d.lat],
          getRadius: (d) => Math.max(4000, Math.sqrt(d.capacityMw) * 300),
          getFillColor: (d) => {
            const base = POWER_PLANT_COLORS[d.type] ?? [148, 163, 184, 200];
            if (d.status === 'offline' || d.status === 'decommissioned') {
              return [base[0], base[1], base[2], 80] as [number, number, number, number];
            }
            return base;
          },
          getLineColor: (d) => {
            if (d.status === 'offline') return [239, 68, 68, 180] as [number, number, number, number];
            if (d.status === 'reduced') return [249, 115, 22, 180] as [number, number, number, number];
            return [255, 255, 255, 40] as [number, number, number, number];
          },
          radiusMinPixels: 3,
          radiusMaxPixels: 18,
          onHover: (info) => {
            if (info.object) {
              const p = info.object as PowerPlant;
              const si = p.status === 'operational' ? 'ONLINE' : p.status === 'offline' ? 'OFFLINE' : p.status === 'reduced' ? 'REDUCED' : 'DECOM';
              setTip(info.x, info.y, {
                header: p.name,
                subheader: p.country,
                badgeText: si,
                badgeColor: p.status === 'operational' ? 'green' : p.status === 'offline' ? 'red' : 'orange',
                details: [
                  { label: 'Type', value: p.type.toUpperCase(), highlight: p.type === 'nuclear' },
                  { label: 'Capacity', value: `${p.capacityMw.toLocaleString()} MW` },
                  { label: 'Status', value: si, highlight: p.status !== 'operational' },
                ],
              });
            } else clearTip();
          },
        }),
      );
    }

    // ── Nuclear Assets ───────────────────────────────────────────────────────
    if (visibleLayers.has('nuclear')) {
      result.push(
        new ScatterplotLayer<NuclearAsset>({
          id: 'nuclear',
          data: nuclearAssets,
          pickable: true,
          opacity: 0.9,
          stroked: true,
          lineWidthMinPixels: 2,
          getPosition: (d) => [d.lon, d.lat],
          getRadius: 6000,
          getFillColor: NUCLEAR_ASSET_COLOR,
          getLineColor: [252, 211, 77, 200],
          radiusMinPixels: 4,
          radiusMaxPixels: 12,
          onHover: (info) => {
            if (info.object) {
              const n = info.object as NuclearAsset;
              setTip(info.x, info.y, {
                header: `☢ ${n.name}`,
                subheader: n.country,
                badgeText: 'NUCLEAR',
                badgeColor: 'red',
                details: [
                  { label: 'Type', value: n.type.replace(/-/g, ' ').toUpperCase(), highlight: true },
                  { label: 'Country', value: n.country },
                  ...(n.rangeKm ? [{ label: 'Range', value: `${n.rangeKm.toLocaleString()} km` }] : []),
                ],
              });
            } else clearTip();
          },
        }),
      );
    }

    // ── Submarine Cables ─────────────────────────────────────────────────────
    if (visibleLayers.has('submarine-cables')) {
      result.push(
        new PathLayer<SubmarineCable>({
          id: 'submarine-cables',
          data: submarineCables,
          pickable: true,
          widthMinPixels: 1,
          widthMaxPixels: 3,
          getPath: (d) => d.path as [number, number][],
          getColor: (d) => CABLE_COLORS[d.status] ?? [99, 102, 241, 150],
          getWidth: () => 2,
          onHover: (info) => {
            if (info.object) {
              const c = info.object as SubmarineCable;
              setTip(info.x, info.y, {
                header: `Cable: ${c.name}`,
                subheader: c.owner,
                badgeText: c.status.toUpperCase(),
                badgeColor: c.status === 'active' ? 'green' : c.status === 'disrupted' ? 'orange' : 'red',
                details: [
                  { label: 'Capacity', value: c.capacity },
                  { label: 'Status', value: c.status.toUpperCase(), highlight: c.status !== 'active' },
                  { label: 'Owner', value: c.owner },
                ],
              });
            } else clearTip();
          },
        }),
      );
    }

    // NOTE: 'aircraft-routes' 가짜 시뮬레이션 레이어 제거됨 — 실제 항공기는 'aircraft' 레이어 (adsb.lol).

    // ── ACLED Conflict Events ─────────────────────────────────────────────────
    if (visibleLayers.has('acled')) {
      // Outer glow
      result.push(
        new ScatterplotLayer<AcledEvent>({
          id: 'acled-glow',
          data: acledEvents,
          pickable: false,
          getPosition: (d) => [d.lon, d.lat],
          getRadius: (d) => d.fatalities > 10 ? 25000 : d.fatalities > 0 ? 15000 : 10000,
          getFillColor: (d) => {
            const base = ACLED_COLORS[d.event_type] ?? [220, 38, 38, 220];
            return [base[0], base[1], base[2], 40] as [number, number, number, number];
          },
          radiusMinPixels: 6,
          radiusMaxPixels: 60,
        }),
      );
      // Main dot
      result.push(
        new ScatterplotLayer<AcledEvent>({
          id: 'acled-events',
          data: acledEvents,
          pickable: true,
          getPosition: (d) => [d.lon, d.lat],
          getRadius: (d) => d.fatalities > 50 ? 12000 : d.fatalities > 10 ? 8000 : 5000,
          getFillColor: (d) => ACLED_COLORS[d.event_type] ?? [220, 38, 38, 220],
          radiusMinPixels: 4,
          radiusMaxPixels: 30,
          onHover: (info) => {
            if (info.object) {
              const e = info.object as AcledEvent;
              setTip(info.x, info.y, {
                header: `⚔ ${e.sub_event_type}`,
                subheader: `${e.location}, ${e.country}`,
                badgeText: e.event_type,
                badgeColor: e.severity === 'critical' ? 'red' : e.severity === 'high' ? 'orange' : 'yellow',
                details: [
                  { label: 'Date', value: e.event_date },
                  { label: 'Actor 1', value: e.actor1, highlight: true },
                  ...(e.actor2 ? [{ label: 'Actor 2', value: e.actor2 }] : []),
                  { label: 'Fatalities', value: String(e.fatalities), highlight: e.fatalities > 0 },
                  { label: 'Source', value: e.source },
                  ...(e.notes ? [{ label: 'Notes', value: e.notes.slice(0, 200) }] : []),
                ],
              });
            } else clearTip();
          },
        }),
      );
    }

    // ── NOTAM Flight Restrictions ─────────────────────────────────────────────
    if (visibleLayers.has('notam')) {
      // Zone circle (stroked)
      result.push(
        new ScatterplotLayer<NotamItem>({
          id: 'notam-zones',
          data: notams.filter((n) => n.isActive),
          pickable: true,
          stroked: true,
          filled: true,
          lineWidthMinPixels: 1,
          getPosition: (d) => [d.lon, d.lat],
          getRadius: (d) => d.radiusNm * 1852, // NM → meters
          getFillColor: (d) => {
            const c = NOTAM_CATEGORY_COLORS[d.category];
            return [c[0], c[1], c[2], 25] as [number, number, number, number];
          },
          getLineColor: (d) => NOTAM_CATEGORY_COLORS[d.category],
          radiusMinPixels: 4,
          radiusMaxPixels: 150,
          onHover: (info) => {
            if (info.object) {
              const n = info.object as NotamItem;
              setTip(info.x, info.y, {
                header: `✈ NOTAM ${n.id}`,
                subheader: `${n.location} — ${n.category.toUpperCase()}`,
                badgeText: n.type,
                badgeColor: n.category === 'restricted' ? 'red' : n.category === 'military' ? 'orange' : 'yellow',
                details: [
                  { label: 'Radius', value: `${n.radiusNm} NM`, highlight: true },
                  ...(n.altitudeMin || n.altitudeMax ? [{ label: 'Altitude', value: `${n.altitudeMin ?? 'SFC'} – ${n.altitudeMax ?? 'UNLIM'}` }] : []),
                  { label: 'Valid from', value: new Date(n.startDate).toLocaleString() },
                  ...(n.endDate ? [{ label: 'Until', value: new Date(n.endDate).toLocaleString() }] : []),
                  { label: 'Text', value: n.text.slice(0, 300) },
                ],
              });
            } else clearTip();
          },
        }),
      );
    }

    // ── Military Aircraft (adsbexchange) ──────────────────────────────────────
    if (visibleLayers.has('military-aircraft') && militaryAircraft.length > 0) {
      result.push(
        new ScatterplotLayer<Aircraft>({
          id: 'military-aircraft',
          data: militaryAircraft,
          pickable: true,
          getPosition: (d) => [d.lon, d.lat],
          getRadius: () => 20000,
          getFillColor: () => [167, 139, 250, 230] as [number, number, number, number], // purple
          radiusMinPixels: 5,
          radiusMaxPixels: 18,
          onHover: (info) => {
            if (info.object) {
              const a = info.object as Aircraft;
              setTip(info.x, info.y, {
                header: `✈ ${a.callsign}`,
                subheader: 'Military Aircraft',
                badgeText: 'MIL',
                badgeColor: 'orange',
                details: [
                  { label: 'Callsign', value: a.callsign, highlight: true },
                  { label: 'Country', value: a.country || 'Unknown' },
                  { label: 'Altitude', value: formatAltitude(a.altitudeFt), highlight: true },
                  { label: 'Speed', value: formatSpeed(a.speedKt) },
                  { label: 'Heading', value: `${a.heading}°` },
                ],
              });
            } else clearTip();
          },
        }),
      );
    }

    // ── Cyber Threats (ArcLayer — Recorded Future style) ─────────────────────
    if (visibleLayers.has('cyber-threats')) {
      // Arc: origin country → target country
      result.push(
        new ArcLayer<CyberThreat>({
          id: 'cyber-threats-arc',
          data: cyberThreats.filter((c) => c.active),
          pickable: true,
          getSourcePosition: (d) => [d.originLon, d.originLat],
          getTargetPosition: (d) => [d.targetLon, d.targetLat],
          getSourceColor: (d) => {
            if (d.severity === 'critical') return [168, 85, 247, 200];
            if (d.severity === 'high')     return [139, 92, 246, 160];
            return [99, 102, 241, 120];
          },
          getTargetColor: (d) => {
            if (d.severity === 'critical') return [236, 72, 153, 240];
            if (d.severity === 'high')     return [217, 70, 239, 200];
            return [167, 139, 250, 150];
          },
          getWidth: (d) => d.severity === 'critical' ? 2.5 : d.severity === 'high' ? 1.5 : 1,
          widthMinPixels: 1,
          widthMaxPixels: 4,
          getHeight: 0.4,
          onHover: (info) => {
            if (info.object) {
              const c = info.object as CyberThreat;
              setTip(info.x, info.y, {
                header: `⚡ ${c.name}`,
                subheader: `${c.threatActor} → ${c.targetSector}`,
                badgeText: c.type.replace(/-/g, ' ').toUpperCase(),
                badgeColor: c.severity === 'critical' ? 'red' : 'orange',
                details: [
                  { label: 'Actor', value: c.threatActor, highlight: true },
                  { label: 'Origin', value: c.originCountry },
                  { label: 'Target', value: `${c.targetCountry} / ${c.targetSector}` },
                  { label: 'Type', value: c.type.replace(/-/g, ' ').toUpperCase() },
                  { label: 'Severity', value: c.severity.toUpperCase(), highlight: c.severity === 'critical' },
                  ...(c.cveIds?.length ? [{ label: 'CVEs', value: c.cveIds.join(', ') }] : []),
                ],
                newsItems: [{ title: c.description, source: 'Threat Intel', publishedAt: c.timestamp }],
              });
            } else clearTip();
          },
        }),
      );
      // Impact dot at target
      result.push(
        new ScatterplotLayer<CyberThreat>({
          id: 'cyber-threats-target',
          data: cyberThreats.filter((c) => c.active),
          pickable: false,
          opacity: 0.85,
          stroked: true,
          lineWidthMinPixels: 1,
          getPosition: (d) => [d.targetLon, d.targetLat],
          getRadius: 5000,
          getFillColor: (d) => d.severity === 'critical' ? [236, 72, 153, 220] : [168, 85, 247, 180],
          getLineColor: [255, 255, 255, 60],
          radiusMinPixels: 3,
          radiusMaxPixels: 10,
        }),
      );
    }

    // ── World Cities (population bubbles) ────────────────────────────────────
    if (visibleLayers.has('cities')) {
      result.push(
        new ScatterplotLayer<WorldCity>({
          id: 'cities',
          data: worldCities,
          pickable: true,
          opacity: 0.85,
          stroked: true,
          lineWidthMinPixels: 1,
          getPosition: (d) => [d.lon, d.lat],
          getRadius: (d) => Math.max(40_000, d.population * 8_000),
          getFillColor: (d) => {
            if (d.strategicImportance === 'critical') return [250, 204, 21, 60] as [number, number, number, number];
            if (d.strategicImportance === 'high')     return [251, 146, 60, 50] as [number, number, number, number];
            return [148, 163, 184, 35] as [number, number, number, number];
          },
          getLineColor: (d) => {
            if (d.strategicImportance === 'critical') return [250, 204, 21, 180] as [number, number, number, number];
            if (d.strategicImportance === 'high')     return [251, 146, 60, 140] as [number, number, number, number];
            return [100, 116, 139, 100] as [number, number, number, number];
          },
          radiusMinPixels: 3,
          radiusMaxPixels: 40,
          onHover: (info) => {
            if (info.object) {
              const c = info.object as WorldCity;
              setTip(info.x, info.y, {
                header: `${c.isCapital ? '🏛 ' : '🏙 '}${c.name}`,
                subheader: c.country,
                badgeText: c.strategicImportance.toUpperCase(),
                badgeColor: c.strategicImportance === 'critical' ? 'yellow' : c.strategicImportance === 'high' ? 'orange' : 'blue',
                details: [
                  { label: 'Population', value: `${c.population.toFixed(1)}M`, highlight: true },
                  { label: 'Country', value: c.country },
                  ...(c.isCapital ? [{ label: 'Role', value: 'National Capital' }] : []),
                  ...(c.notes ? [{ label: 'Notes', value: c.notes }] : []),
                ],
              });
            } else clearTip();
          },
        }),
      );
    }

    // ── Strategic Chokepoints ────────────────────────────────────────────────
    if (visibleLayers.has('chokepoints')) {
      result.push(
        new ScatterplotLayer<StrategicChokepoint>({
          id: 'chokepoints-glow',
          data: chokepoints,
          pickable: false,
          opacity: 0.3,
          stroked: false,
          getPosition: (d) => [d.lon, d.lat],
          getRadius: 80_000,
          getFillColor: (d) => {
            if (d.threatLevel === 'critical') return [239, 68, 68, 80] as [number, number, number, number];
            if (d.threatLevel === 'high')     return [249, 115, 22, 70] as [number, number, number, number];
            return [234, 179, 8, 60] as [number, number, number, number];
          },
          radiusMinPixels: 12,
          radiusMaxPixels: 60,
        }),
      );
      result.push(
        new ScatterplotLayer<StrategicChokepoint>({
          id: 'chokepoints',
          data: chokepoints,
          pickable: true,
          opacity: 0.95,
          stroked: true,
          lineWidthMinPixels: 2,
          getPosition: (d) => [d.lon, d.lat],
          getRadius: 18_000,
          getFillColor: (d) => {
            if (d.threatLevel === 'critical') return [239, 68, 68, 220] as [number, number, number, number];
            if (d.threatLevel === 'high')     return [249, 115, 22, 200] as [number, number, number, number];
            return [234, 179, 8, 180] as [number, number, number, number];
          },
          getLineColor: [255, 255, 255, 120],
          radiusMinPixels: 7,
          radiusMaxPixels: 18,
          onHover: (info) => {
            if (info.object) {
              const c = info.object as StrategicChokepoint;
              setTip(info.x, info.y, {
                header: `⚓ ${c.name}`,
                subheader: c.nameKo,
                badgeText: c.threatLevel.toUpperCase(),
                badgeColor: c.threatLevel === 'critical' ? 'red' : c.threatLevel === 'high' ? 'orange' : 'yellow',
                details: [
                  { label: 'Type', value: c.type.toUpperCase(), highlight: true },
                  { label: 'Ships/Day', value: c.dailyShips.toLocaleString(), highlight: true },
                  ...(c.oilMbdEquivalent ? [{ label: 'Oil Transit', value: `${c.oilMbdEquivalent} Mb/d`, highlight: true }] : []),
                  { label: 'Controlled By', value: c.controlledBy.join(', ') },
                  { label: 'Intel', value: c.strategicNotes },
                ],
              });
            } else clearTip();
          },
        }),
      );
    }

    // ── Carrier Groups ───────────────────────────────────────────────────────
    if (visibleLayers.has('carriers')) {
      // Carrier group glow halo
      result.push(
        new ScatterplotLayer<CarrierGroup>({
          id: 'carriers-halo',
          data: carrierGroups,
          pickable: false,
          opacity: 0.25,
          stroked: false,
          getPosition: (d) => [d.lon, d.lat],
          getRadius: 200_000,
          getFillColor: (d) => {
            if (d.country === 'USA')   return [56, 189, 248, 60] as [number, number, number, number];
            if (d.country === 'China') return [239, 68, 68, 60] as [number, number, number, number];
            return [167, 139, 250, 50] as [number, number, number, number];
          },
          radiusMinPixels: 14,
          radiusMaxPixels: 50,
        }),
      );
      // Carrier marker
      result.push(
        new ScatterplotLayer<CarrierGroup>({
          id: 'carriers',
          data: carrierGroups,
          pickable: true,
          opacity: 1,
          stroked: true,
          lineWidthMinPixels: 2,
          getPosition: (d) => [d.lon, d.lat],
          getRadius: 25_000,
          getFillColor: (d) => {
            if (d.country === 'USA')    return [56, 189, 248, 240] as [number, number, number, number];
            if (d.country === 'China')  return [239, 68, 68, 240] as [number, number, number, number];
            if (d.country === 'UK')     return [99, 102, 241, 240] as [number, number, number, number];
            if (d.country === 'France') return [34, 197, 94, 240] as [number, number, number, number];
            if (d.country === 'India')  return [249, 115, 22, 240] as [number, number, number, number];
            return [148, 163, 184, 220] as [number, number, number, number];
          },
          getLineColor: [255, 255, 255, 200],
          radiusMinPixels: 8,
          radiusMaxPixels: 20,
          onHover: (info) => {
            if (info.object) {
              const cg = info.object as CarrierGroup;
              setTip(info.x, info.y, {
                header: `🛳 ${cg.carrierName}`,
                subheader: `${cg.groupName} — ${cg.region}`,
                badgeText: cg.country,
                badgeColor: cg.country === 'USA' ? 'blue' : cg.country === 'China' ? 'red' : 'green',
                details: [
                  { label: 'Air Wing', value: cg.airWing, highlight: true },
                  { label: 'Mission', value: cg.mission },
                  { label: 'Destroyers', value: cg.destroyers.join(', ') },
                  ...(cg.cruisers.length ? [{ label: 'Cruisers', value: cg.cruisers.join(', ') }] : []),
                  { label: 'Submarines', value: cg.submarines.join(', ') },
                  { label: 'Heading', value: `${cg.heading}°` },
                ],
              });
            } else clearTip();
          },
        }),
      );
    }

    // ── Selected Country Highlight (glow + ring — always rendered when selected) ──
    if (selectedCountry) {
      const stanceColorMap: Record<string, [number, number, number]> = {
        western: [56, 189, 248], eastern: [239, 68, 68], neutral: [100, 116, 139],
        revisionist: [251, 146, 60], rogue: [239, 68, 68], pivotal: [167, 139, 250],
      };
      const sc = stanceColorMap[selectedCountry.geopoliticalStance] ?? [100, 116, 139];
      // Outer glow
      result.push(new ScatterplotLayer<CountryProfile>({
        id: 'country-selected-glow',
        data: [selectedCountry],
        pickable: false,
        opacity: 0.2,
        stroked: false,
        getPosition: (d) => [d.capitalLon, d.capitalLat],
        getRadius: 450_000,
        getFillColor: [sc[0], sc[1], sc[2], 50] as [number, number, number, number],
        radiusMinPixels: 30,
        radiusMaxPixels: 200,
      }));
      // Selection ring
      result.push(new ScatterplotLayer<CountryProfile>({
        id: 'country-selected-ring',
        data: [selectedCountry],
        pickable: false,
        opacity: 1,
        stroked: true,
        filled: false,
        lineWidthMinPixels: 2,
        getPosition: (d) => [d.capitalLon, d.capitalLat],
        getRadius: 220_000,
        getLineColor: [sc[0], sc[1], sc[2], 210] as [number, number, number, number],
        radiusMinPixels: 18,
        radiusMaxPixels: 130,
      }));
    }

    // ── Country Capitals (clickable — always shown, subtle) ──────────────────
    if (countryProfiles.length > 0) {
      result.push(
        new ScatterplotLayer<CountryProfile>({
          id: 'country-capitals',
          data: countryProfiles,
          pickable: true,
          opacity: compareMode ? 0.95 : 0.7,
          stroked: true,
          lineWidthMinPixels: compareMode ? 2 : 1,
          getPosition: (d) => [d.capitalLon, d.capitalLat],
          getRadius: 12_000,
          getFillColor: (d) => {
            if (compareMode) return [56, 189, 248, 200] as [number, number, number, number]; // cyan pulse in compare mode
            if (d.powerIndex >= 80)  return [250, 204, 21, 160] as [number, number, number, number];
            if (d.powerIndex >= 60)  return [251, 146, 60, 140] as [number, number, number, number];
            return [100, 116, 139, 120] as [number, number, number, number];
          },
          getLineColor: compareMode
            ? [56, 189, 248, 255]
            : [255, 255, 255, 60],
          radiusMinPixels: compareMode ? 6 : 4,
          radiusMaxPixels: 12,
          onHover: (info) => {
            // Minimal hover on capital dot — just name badge (no detailed data)
            if (info.object) {
              const p = info.object as CountryProfile;
              setTip(info.x, info.y, {
                header: `${p.flagEmoji} ${p.name}`,
                subheader: compareMode ? '← Click to compare' : `${p.capitalCity} · Click for intel`,
                badgeText: compareMode ? 'COMPARE' : `Idx ${p.powerIndex}`,
                badgeColor: compareMode ? 'blue' : undefined,
                details: [],
              });
            } else clearTip();
          },
          onClick: (info) => {
            if (!info.object) return;
            const p = info.object as CountryProfile;
            if (compareMode) {
              setCompareCountry(p);
              setCompareMode(false);
            } else {
              selectCountry(p);
            }
          },
        }),
      );
      // ── Large transparent hit zones (easy to click anywhere on country) ───
      result.push(
        new ScatterplotLayer<CountryProfile>({
          id: 'country-hit-zones',
          data: countryProfiles,
          pickable: true,
          opacity: 0,
          stroked: false,
          getPosition: (d) => [d.capitalLon, d.capitalLat],
          getRadius: 400_000,
          getFillColor: [0, 0, 0, 0] as [number, number, number, number],
          getLineColor: [0, 0, 0, 0] as [number, number, number, number],
          radiusMinPixels: 20,
          radiusMaxPixels: 150,
          onHover: (info) => {
            // Hit zone hover: show flag + name + capital (no heavy data)
            if (info.object) {
              const p = info.object as CountryProfile;
              setTip(info.x, info.y, {
                header: `${p.flagEmoji} ${p.name}`,
                subheader: compareMode ? '← Click to compare' : `${p.capitalCity} · Click for intel`,
                badgeText: compareMode ? 'COMPARE' : `Idx ${p.powerIndex}`,
                badgeColor: compareMode ? 'blue' : undefined,
                details: [],
              });
            } else clearTip();
          },
          onClick: (info) => {
            if (!info.object) return;
            const p = info.object as CountryProfile;
            if (compareMode) { setCompareCountry(p); setCompareMode(false); }
            else { selectCountry(p); }
          },
        }),
      );
    }

    // ── Webcam Pins ───────────────────────────────────────────────────────────
    if (visibleLayers.has('webcams')) {
      // Use Windy webcams (loaded by LiveCamPanel) or adapt PUBLIC_CAMS as fallback
      const camPins: WindyWebcam[] = windyWebcams.length > 0
        ? windyWebcams
        : PUBLIC_CAMS.map((c) => ({
            webcamId: c.id, title: c.name, status: 'active' as const,
            location: { city: c.city, region: '', country: c.country, continent: c.region, latitude: c.lat, longitude: c.lon, timezone: '' },
            images: { current: { thumbnail: '', preview: '' }, daylight: true },
            player: { day: { embed: c.videoId ? `https://www.youtube.com/embed/${c.videoId}?autoplay=1&mute=1&controls=1&rel=0` : '', web: c.websiteUrl ?? '' } },
            urls: { website: c.websiteUrl ?? '' },
          }));

      // Glow ring
      result.push(
        new ScatterplotLayer<WindyWebcam>({
          id: 'webcam-glow',
          data: camPins,
          pickable: false,
          opacity: 0.25,
          stroked: false,
          getPosition: (d) => [d.location.longitude, d.location.latitude],
          getRadius: 40_000,
          getFillColor: [34, 211, 238, 60] as [number, number, number, number],
          radiusMinPixels: 6,
          radiusMaxPixels: 22,
        }),
      );
      // Camera dot
      result.push(
        new ScatterplotLayer<WindyWebcam>({
          id: 'webcam-pins',
          data: camPins,
          pickable: true,
          opacity: 0.95,
          stroked: true,
          lineWidthMinPixels: 1,
          getPosition: (d) => [d.location.longitude, d.location.latitude],
          getRadius: 12_000,
          getFillColor: (d) => (d.player.day.embed
            ? [34, 211, 238, 230] as [number, number, number, number]
            : [100, 116, 139, 180] as [number, number, number, number]
          ),
          getLineColor: [255, 255, 255, 120] as [number, number, number, number],
          radiusMinPixels: 4,
          radiusMaxPixels: 10,
          onHover: (info) => {
            if (info.object) {
              const c = info.object as WindyWebcam;
              setTip(info.x, info.y, {
                header: `📷 ${c.title}`,
                subheader: `${c.location.city}, ${c.location.country}`,
                badgeText: c.player.day.embed ? 'LIVE' : 'LINK',
                badgeColor: c.player.day.embed ? 'blue' : undefined,
                details: [{ label: 'Region', value: c.location.continent || c.location.country }],
              });
            } else clearTip();
          },
          onClick: (info) => {
            if (info.object) {
              setSelectedMapCam(info.object as WindyWebcam);
              clearTip();
            }
          },
        }),
      );
    }

    // ── Troop Movements (ArcLayer, bidirectional) ─────────────────────────────
    if (visibleLayers.has('troop-movements')) {
      // Forward arc
      result.push(
        new ArcLayer<TroopMovement>({
          id: 'troops-arc-forward',
          data: isSimActive ? simTroopMovements : [...troopMovements, ...simTroopMovements],
          pickable: true,
          getSourcePosition: (d) => [d.fromLon, d.fromLat],
          getTargetPosition: (d) => [d.toLon, d.toLat],
          getSourceColor: (d) => {
            const c = MOVEMENT_TYPE_COLORS[d.type] ?? [251, 146, 60];
            return [c[0], c[1], c[2], 220];
          },
          getTargetColor: (d) => {
            const c = MOVEMENT_TYPE_COLORS[d.type] ?? [251, 146, 60];
            return [c[0], c[1], c[2], 80];
          },
          getWidth: (d) => {
            const w: Record<string, number> = { corps: 4, division: 3, brigade: 2, battalion: 1.5, company: 1 };
            return w[d.scale] ?? 2;
          },
          widthMinPixels: 1.5,
          widthMaxPixels: 6,
          getHeight: 0.5,
          onHover: (info) => {
            if (info.object) {
              const t = info.object as TroopMovement;
              setTip(info.x, info.y, {
                header: `${t.fromCountry} → ${t.toCountry}`,
                subheader: t.description,
                badgeText: t.scale.toUpperCase(),
                badgeColor: t.severity === 'critical' ? 'red' : 'orange',
                details: [
                  { label: 'Type', value: t.type.toUpperCase(), highlight: true },
                  { label: 'Scale', value: t.scale.toUpperCase() },
                  { label: 'Severity', value: t.severity.toUpperCase() },
                  { label: 'Since', value: t.startDate },
                ],
                newsItems: t.relatedNews,
              });
            } else clearTip();
          },
        }),
      );
      // Return arc (lighter, reverse direction)
      result.push(
        new ArcLayer<TroopMovement>({
          id: 'troops-arc-return',
          data: troopMovements.filter((t) => t.severity === 'critical' || t.severity === 'high'),
          pickable: false,
          getSourcePosition: (d) => [d.toLon, d.toLat],
          getTargetPosition: (d) => [d.fromLon, d.fromLat],
          getSourceColor: (d) => {
            const c = MOVEMENT_TYPE_COLORS[d.type] ?? [251, 146, 60];
            return [c[0], c[1], c[2], 40];
          },
          getTargetColor: (d) => {
            const c = MOVEMENT_TYPE_COLORS[d.type] ?? [251, 146, 60];
            return [c[0], c[1], c[2], 100];
          },
          getWidth: 1,
          widthMinPixels: 0.5,
          widthMaxPixels: 2,
          getHeight: 0.3,
        }),
      );
    }

    // CCTV 는 마지막에 push 한다 = 가장 위에 그려짐 = 픽킹 우선.
    // 앞쪽에 두었더니 국가 폴리곤 등 뒤에 오는 레이어가 클릭을 가로채
    // 카메라를 전혀 집을 수 없었다 (헤드리스 실측으로 확인).
    // ── 전 세계 실시간 교통 CCTV ──────────────────────────────────────────────
    if (visibleLayers.has('traffic-cctv') && trafficCameras.length > 0) {
      result.push(
        new ScatterplotLayer<typeof trafficCameras[0]>({
          id: 'traffic-cctv',
          data: trafficCameras,
          pickable: true,
          stroked: true,
          filled: true,
          getPosition: (d) => [d.lon, d.lat],
          getRadius: 600,
          // 실시간 영상(HLS/mp4)이 있는 카메라는 민트, 정지 프레임은 로즈
          getFillColor: (d) => (d.hlsUrl || d.videoUrl ? [143, 247, 199, 220] : [253, 164, 175, 200]),
          getLineColor: [255, 255, 255, 120],
          lineWidthMinPixels: 1,
          // 2px 는 클릭이 거의 불가능했다 — 집기 좋은 최소 크기로 올린다
          radiusMinPixels: 3.5,
          radiusMaxPixels: 10,
          onClick: (info) => {
            const c = info.object as typeof trafficCameras[0] | undefined;
            if (!c) return false;
            setSelectedCctvId(c.id);
            return true;
          },
          onHover: (info) => {
            if (info.object) {
              const c = info.object as typeof trafficCameras[0];
              const kind = c.hlsUrl ? 'LIVE STREAM' : c.videoUrl ? 'LIVE CLIP' : `FRAME ${c.refreshSec}s`;
              setTip(info.x, info.y, {
                header: c.name,
                subheader: `${c.provider} · ${c.region}`,
                badgeText: kind,
                badgeColor: c.hlsUrl || c.videoUrl ? 'green' : 'blue',
                details: [
                  { label: 'Source', value: c.provider, highlight: true },
                  { label: '', value: 'Click to watch →' },
                ],
              });
            } else clearTip();
          },
        }),
      );
    }

    return result;
  }, [
    earthquakes, wildfires, aircraft, ships, navalVessels, fighterJets,
    powerPlants, conflictZones, droneZones, nuclearAssets, submarineCables,
    troopMovements, simTroopMovements, simConflictZones, cyberThreats,
    worldCities, chokepoints, carrierGroups, countryProfiles, selectedCountry,
    compareMode, selectCountry, setCompareCountry, setCompareMode,
    visibleLayers, setTip, clearTip, windyWebcams, isSimActive,
    selectedAircraftId, setSelectedAircraftId, trafficCameras, setSelectedCctvId,
  ]);

  // Missile layers in separate useMemo for 10fps performance
  const missileLayers = useMemo(() => {
    if (!visibleLayers.has('missiles')) return [];
    const result: AnyLayer[] = [];

    const allMissiles = isSimActive ? simMissiles : [...missiles, ...simMissiles];
    const flying = allMissiles.filter((m) => m.status === 'flying');
    const impacting = allMissiles.filter((m) => m.status === 'impact');

    // 궤적 어디를 hover해도 툴팁 표시 (arc + head 공유 핸들러)
    const missileHover = (info: { object?: unknown; x: number; y: number }) => {
      if (info.object) {
        const m = info.object as MissileTrajectory;
        const reportedAt = m.relatedNews[0]?.publishedAt;
        const label = m.type === 'drone' ? 'drone' : `${m.type} missile`;
        setTip(info.x, info.y, {
          header: `${m.type === 'drone' ? '🛩' : '🚀'} ${m.firedBy} → ${m.targetName}`,
          subheader: reportedAt
            ? `${label.toUpperCase()} — reported ${timeAgo(reportedAt)}`
            : label.toUpperCase(),
          badgeText: 'LIVE OSINT',
          badgeColor: m.type === 'hypersonic' ? 'red' : 'orange',
          details: [
            { label: 'Fired By', value: m.firedBy, highlight: true },
            { label: 'Target', value: m.targetName, highlight: true },
            { label: 'Type', value: m.type.toUpperCase() },
            ...(reportedAt ? [{ label: 'Reported', value: timeAgo(reportedAt), highlight: true }] : []),
          ],
          newsItems: m.relatedNews,
        });
      } else clearTip();
    };

    if (flying.length > 0) {
      // Arc trajectory — pickable: 머리 대신 궤적 전체가 hover 대상
      result.push(
        new ArcLayer<MissileTrajectory>({
          id: 'missiles-arc',
          data: flying,
          pickable: true,
          getSourcePosition: (d) => [d.originLon, d.originLat],
          getTargetPosition: (d) => [d.targetLon, d.targetLat],
          getSourceColor: (d) => {
            const c = MISSILE_TYPE_COLORS[d.type] ?? [239, 68, 68];
            return [c[0], c[1], c[2], 30];
          },
          getTargetColor: (d) => {
            const c = MISSILE_TYPE_COLORS[d.type] ?? [239, 68, 68];
            return [c[0], c[1], c[2], 180];
          },
          getWidth: (d) => d.type === 'hypersonic' ? 2 : 1.5,
          widthMinPixels: 1,
          widthMaxPixels: 3,
          getHeight: (d) => d.type === 'ballistic' ? 2.5 : d.type === 'hypersonic' ? 1.0 : d.type === 'drone' ? 0.15 : 0.6,
          onHover: missileHover,
        }),
      );
      // Missile head (current position)
      result.push(
        new ScatterplotLayer<MissileTrajectory>({
          id: 'missiles-head',
          data: flying,
          pickable: true,
          opacity: 1,
          stroked: true,
          lineWidthMinPixels: 1,
          getPosition: (d) => [d.currentLon, d.currentLat],
          getRadius: 3500,
          getFillColor: (d) => {
            const c = MISSILE_TYPE_COLORS[d.type] ?? [239, 68, 68];
            return [c[0], c[1], c[2], 255];
          },
          getLineColor: [255, 255, 255, 180],
          radiusMinPixels: 3,
          radiusMaxPixels: 9,
          updateTriggers: { getPosition: [missiles, simMissiles] },
          onHover: missileHover,
        }),
      );
    }

    // Impact explosion effect
    if (impacting.length > 0) {
      result.push(
        new ScatterplotLayer<MissileTrajectory>({
          id: 'missiles-impact',
          data: impacting,
          pickable: false,
          opacity: 0.85,
          stroked: false,
          getPosition: (d) => [d.targetLon, d.targetLat],
          getRadius: 20000,
          getFillColor: [255, 200, 50, 220],
          radiusMinPixels: 8,
          radiusMaxPixels: 30,
          updateTriggers: { getPosition: [missiles, simMissiles] },
        }),
      );
    }

    return result;
  }, [missiles, simMissiles, isSimActive, visibleLayers, setTip, clearTip]);

  // Drone attack layers (separate useMemo — 150ms animation)
  const droneLayers = useMemo(() => {
    if (!visibleLayers.has('drone-attacks')) return [];
    const result: AnyLayer[] = [];

    const inbound = droneAttacks.filter((d) => d.status === 'inbound');
    const impacts = droneAttacks.filter((d) => d.status === 'impact');

    // Trajectory arc (flat, low-altitude drones)
    if (inbound.length > 0) {
      result.push(
        new ArcLayer<DroneAttack>({
          id: 'drone-arc',
          data: inbound,
          pickable: true,
          getSourcePosition: (d) => [d.originLon, d.originLat],
          getTargetPosition: (d) => [d.targetLon, d.targetLat],
          getSourceColor: [250, 204, 21, 180],    // yellow (drone exhaust)
          getTargetColor: [251, 146, 60, 220],    // orange
          getWidth: 1.5,
          getHeight: 0.08,                        // very low arc — drones fly low
          widthMinPixels: 1,
          widthMaxPixels: 3,
          onHover: (info) => {
            if (info.object) {
              const d = info.object as DroneAttack;
              setTip(info.x, info.y, {
                header: `🚁 ${DRONE_TYPE_LABELS[d.type]}`,
                subheader: `${d.firedBy} → ${d.targetName}`,
                badgeText: 'INBOUND',
                badgeColor: 'yellow',
                details: [
                  { label: 'Type', value: DRONE_TYPE_LABELS[d.type] },
                  { label: 'Origin', value: d.firedBy },
                  { label: 'Target', value: d.targetName, highlight: true },
                  { label: 'Progress', value: `${Math.round(d.progress * 100)}%` },
                  { label: 'SNS Sources', value: d.snsSources.join(', ') },
                ],
                newsItems: d.relatedNews,
              });
            } else clearTip();
          },
        }),
      );
    }

    // Current drone position dot
    result.push(
      new ScatterplotLayer<DroneAttack>({
        id: 'drone-position',
        data: inbound,
        pickable: false,
        getPosition: (d) => [d.currentLon, d.currentLat],
        getRadius: 5000,
        getFillColor: [250, 204, 21, 220],
        radiusMinPixels: 3,
        radiusMaxPixels: 7,
      }),
    );

    // Impact flash
    if (impacts.length > 0) {
      result.push(
        new ScatterplotLayer<DroneAttack>({
          id: 'drone-impact',
          data: impacts,
          pickable: false,
          getPosition: (d) => [d.targetLon, d.targetLat],
          getRadius: 25000,
          getFillColor: [251, 146, 60, 200],
          radiusMinPixels: 5,
          radiusMaxPixels: 18,
        }),
      );
    }

    return result;
  }, [droneAttacks, visibleLayers, setTip, clearTip]);

  // Globe base tile layer — renders earth surface when _GlobeView is active
  const globeBaseTile = useMemo(() => {
    if (!globeMode) return null;
    const basemap = getGlobeBasemap(globeBasemap);
    return new TileLayer({
      id: `globe-base-tiles-${basemap.id}`,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: basemap.url as any,
      minZoom: 0,
      maxZoom: basemap.maxZoom,
      tileSize: 256,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      renderSubLayers: (props: any) => {
        const { west, south, east, north } = props.tile.bbox;
        return new BitmapLayer(props, {
          data: undefined,
          image: props.data,
          bounds: [west, south, east, north],
        });
      },
    });
  }, [globeMode, globeBasemap]);

  // Crisis alert map markers — always rendered when alerts are active (not behind layer toggle)
  const crisisAlertLayers = useMemo(() => {
    const active = crisisAlerts.filter((a) => !a.dismissed && a.lat != null && a.lon != null);
    if (!active.length) return [];

    const getColor = (level: string): [number, number, number, number] => {
      if (level === 'extreme') return [220, 38, 38, 255];    // red
      if (level === 'critical') return [249, 115, 22, 255];  // orange
      return [234, 179, 8, 255];                              // yellow
    };

    return [
      // Outer halo ring (large, semi-transparent)
      new ScatterplotLayer<typeof active[0]>({
        id: 'crisis-halo',
        data: active,
        pickable: false,
        stroked: true,
        filled: false,
        getPosition: (d) => [d.lon!, d.lat!],
        getRadius: (d) => d.level === 'extreme' ? 280_000 : d.level === 'critical' ? 200_000 : 140_000,
        getLineColor: (d) => { const c = getColor(d.level); return [c[0], c[1], c[2], 80]; },
        getLineWidth: 3,
        lineWidthMinPixels: 2,
        radiusUnits: 'meters',
      }),
      // Inner solid dot
      new ScatterplotLayer<typeof active[0]>({
        id: 'crisis-dot',
        data: active,
        pickable: true,
        stroked: true,
        filled: true,
        getPosition: (d) => [d.lon!, d.lat!],
        getRadius: (d) => d.level === 'extreme' ? 80_000 : d.level === 'critical' ? 60_000 : 45_000,
        getFillColor: (d) => { const c = getColor(d.level); return [c[0], c[1], c[2], 200]; },
        getLineColor: [255, 255, 255, 180],
        getLineWidth: 2,
        lineWidthMinPixels: 1,
        radiusUnits: 'meters',
        onHover: (info) => {
          if (info.object) {
            const a = info.object as typeof active[0];
            setTip(info.x, info.y, {
              header: a.level === 'extreme' ? '⚠ EXTREME ALERT' : a.level === 'critical' ? '🔴 CRITICAL' : '🟡 WARNING',
              subheader: a.region,
              badgeText: a.level.toUpperCase(),
              badgeColor: a.level === 'extreme' ? 'red' : a.level === 'critical' ? 'orange' : 'yellow',
              details: [
                { label: 'Summary', value: a.summary },
                { label: 'Confidence', value: `${a.confidence}%` },
                { label: 'Sources', value: a.sources.join(', ') },
              ],
            });
          } else { clearTip(); }
        },
      }),
    ];
  }, [crisisAlerts, setTip, clearTip]);

  // ── 센서 광학 포스트프로세싱 ───────────────────────────────
  // GEV 의 Cesium PostProcessStage 대응. 센서가 켜진 동안에만 deck 이
  // 매 프레임 리드로우(_animate)하며, time uniform 을 rAF 로 주입한다.
  const sensorEffect = useMemo(() => {
    if (sensorMode === 'none') return null;
    const pass = SENSOR_PASSES[sensorMode as Exclude<SensorMode, 'none'>];
    if (!pass) return null;
    return new PostProcessEffect(pass, { ...pass.defaultUniforms });
  }, [sensorMode]);

  useEffect(() => {
    if (!sensorEffect || sensorMode === 'none') return;
    const pass = SENSOR_PASSES[sensorMode as Exclude<SensorMode, 'none'>];
    const t0 = performance.now();
    let raf = 0;
    const tick = () => {
      sensorEffect.setProps({
        ...pass.defaultUniforms,
        time: (performance.now() - t0) / 1000,
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [sensorEffect, sensorMode]);

  const effects = useMemo(() => (sensorEffect ? [sensorEffect] : []), [sensorEffect]);

  const allLayers = [
    ...(globeBaseTile ? [globeBaseTile] : []),
    ...staticLayers,
    ...missileLayers,
    ...droneLayers,
    ...crisisAlertLayers,
  ];

  return (
    <div className={`absolute inset-0${compareMode ? ' cursor-crosshair' : ''}`}>
      {compareMode && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 pointer-events-none">
          <div className="bg-sky-950/90 border border-sky-500/60 rounded-lg px-4 py-2 text-[11px] font-semibold text-sky-300 shadow-lg animate-pulse">
            Click a country to compare
          </div>
        </div>
      )}
      {/* Space background for globe mode — must be BEHIND DeckGL canvas */}
      {globeMode && (
        <div
          className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse at 40% 40%, #0d1b3a 0%, #060c1f 60%, #020408 100%)' }}
        />
      )}
      <DeckGL
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        views={globeMode ? [new (GlobeView as any)({ id: 'globe' })] : undefined}
        viewState={viewState as any}
        onViewStateChange={onViewStateChange}
        layers={allLayers}
        effects={effects}
        _animate={Boolean(sensorEffect)}
        controller
        getTooltip={null}
        pickingRadius={8}
        onHover={(info) => {
          const c = info.coordinate;
          setCursorGeo(c ? { lon: c[0], lat: c[1] } : null);
        }}
      >
        {!globeMode && (
          <Map
            ref={mapRef}
            mapStyle={MAP_STYLE}
            attributionControl={false}
            reuseMaps
          />
        )}
      </DeckGL>

      {tooltip && <RichTooltipPanel tip={tooltip} />}

      {/* Floating Webcam Player — opens when a pin is clicked on the map */}
      {selectedMapCam && (
        <div className="fixed left-3 right-3 bottom-16 z-50 md:absolute md:left-2 md:right-auto md:w-72 rounded-lg overflow-hidden shadow-2xl border border-slate-700/60 bg-slate-950 pointer-events-auto">
          {/* Header */}
          <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-slate-800 bg-slate-900/80">
            <div className="flex items-center gap-1.5 min-w-0">
              <div className="min-w-0">
                <div className="text-[11px] font-semibold text-slate-200 truncate">{selectedMapCam.title}</div>
                <div className="text-[9px] text-slate-500">{selectedMapCam.location.city}, {selectedMapCam.location.country}</div>
              </div>
              {selectedMapCam.player.day.embed && (
                <span className="shrink-0 text-[8px] text-cyan-400 font-bold">● LIVE</span>
              )}
            </div>
            <button
              onClick={() => setSelectedMapCam(null)}
              className="shrink-0 ml-2 w-5 h-5 flex items-center justify-center rounded text-slate-500 hover:text-white hover:bg-slate-700 transition-colors text-[11px]"
            >
              ✕
            </button>
          </div>

          {/* Video Player */}
          <div className="relative aspect-video bg-black">
            {selectedMapCam.player.day.embed ? (
              <iframe
                key={selectedMapCam.webcamId}
                src={selectedMapCam.player.day.embed}
                className="absolute inset-0 w-full h-full"
                allow="autoplay; fullscreen"
                allowFullScreen
              />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900 gap-2">
                {selectedMapCam.images?.current?.preview && (
                  <img src={selectedMapCam.images.current.preview} alt={selectedMapCam.title} className="absolute inset-0 w-full h-full object-cover opacity-40" />
                )}
                <div className="relative text-[11px] text-slate-300">{selectedMapCam.location.city}</div>
                {selectedMapCam.urls.website ? (
                  <a
                    href={selectedMapCam.urls.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative px-3 py-1 text-[11px] bg-cyan-900/30 hover:bg-cyan-800/50 text-cyan-300 rounded-full border border-cyan-700/40 transition-colors"
                  >
                    Watch Live ↗
                  </a>
                ) : (
                  <span className="relative text-[10px] text-slate-500">No stream available</span>
                )}
              </div>
            )}
          </div>

          {/* Location info */}
          <div className="px-2.5 py-1 text-[9px] text-slate-500 border-t border-slate-800/60 bg-slate-900/50 truncate">
            {selectedMapCam.location.continent} · {selectedMapCam.location.country}
          </div>
        </div>
      )}
    </div>
  );
};
